import i18n from '../../i18n';
import { TIMEZONES } from '../../utils/timezones';

/**
 * Get the user's timezone from the browser
 * @returns The user's timezone
 */
export const getUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return "UTC";
  }
};

/**
 * Get the closest timezone from our list to the user's timezone
 * @param userTimezone The user's timezone
 * @returns The closest timezone from our list
 */
export const getClosestTimezone = (userTimezone: string): string => {
  // First check if the user's timezone is in our list
  const isKnownTimezone = TIMEZONES.some((timezone) => timezone.value === userTimezone);
  if (isKnownTimezone) {
    return userTimezone;
  }

  // If not, try to find a timezone with the same offset
  try {
    const now = new Date();
    const userOffset = new Date().getTimezoneOffset();

    for (const timezone of TIMEZONES) {
      try {
        // Get the offset for this timezone
        const tzOffset = new Date(
          now.toLocaleString("en-US", { timeZone: timezone.value })
        ).getTimezoneOffset();

        // If the offset matches, use this timezone
        if (tzOffset === userOffset) {
          return timezone.value;
        }
      } catch (e) {
        // Skip this timezone if there's an error
        continue;
      }
    }
  } catch (error) {
    // Fall through to default
  }

  // Default to UTC if we can't find a match
  return "UTC";
};

/**
 * Gets the UTC offset for a timezone (like '+02:00' or '-05:00')
 */
export const getTimezoneOffset = (timezone: string): string => {
  try {
    // Create a date at a specific point in time to avoid DST issues
    const date = new Date('2023-01-01T00:00:00Z');
    
    // Get the timezone and offset
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    });
    
    // Extract the GMT offset from the formatted string
    const formattedDate = formatter.format(date);
    const match = formattedDate.match(/GMT([+-]\d{2}):(\d{2})/);
    
    if (match) {
      return `${match[1]}:${match[2]}`;
    }
    
    // Fallback to calculating the offset manually
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMinutes = (tzDate.getTime() - date.getTime()) / (60 * 1000);
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    
    const sign = offsetMinutes >= 0 ? '+' : '-';
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } catch (error) {
    return '+00:00'; // Default to UTC
  }
};

/**
 * Checks if a timezone is valid
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    // Try to format a date with the timezone
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date());
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get localized timezone labels based on current language
 * @returns Array of timezones with localized labels
 */
export const getLocalizedTimezones = () => {
  return TIMEZONES.map(tz => {
    const offset = getTimezoneOffset(tz.value);
    const zoneName = i18n.t(`availability:timezone.zones.${tz.value}`, tz.value);

    return {
      value: tz.value,
      label: `GMT${offset} ${zoneName}`
    };
  });
};

/**
 * Format timezone offset for display (e.g., "GMT+8", "GMT-5", "GMT+5:30")
 * Removes ":00" suffix for whole hour offsets
 * @param timezone The timezone string (e.g., "Asia/Tokyo")
 * @returns Formatted timezone string (e.g., "GMT+9")
 */
export const formatTimezoneForDisplay = (timezone: string): string => {
  try {
    const offset = getTimezoneOffset(timezone);

    // Remove ":00" suffix for whole hour offsets
    // Keep ":30" or ":45" for half-hour/quarter-hour offsets
    if (offset.endsWith(':00')) {
      return `GMT${offset.slice(0, -3)}`;
    }

    return `GMT${offset}`;
  } catch (error) {
    return 'GMT+0'; // Default to GMT+0
  }
}; 